/**
 * Social profile helpers for position config and public posting forms.
 */
import type {
  Application,
  SocialProfileField,
  SocialProfileSubmission,
  SocialProfileType,
} from '@/types';

export type { SocialProfileField, SocialProfileSubmission, SocialProfileType };

export const SOCIAL_PROFILE_TYPES: SocialProfileType[] = [
  'linkedin',
  'instagram',
  'facebook',
  'tiktok',
  'website',
  'custom',
];

const MAX_SOCIAL_PROFILES = 10;

export function socialProfileFieldKey(field: {
  type: SocialProfileType;
  customLabel?: string | null;
  label?: string | null;
}): string {
  if (field.type === 'custom') {
    return `custom:${(field.customLabel ?? field.label ?? '').trim().toLowerCase()}`;
  }
  return field.type;
}

/** Maps legacy requireLinkedIn boolean into the new array shape. */
export function resolveSocialProfilesFromPosition(raw: {
  socialProfiles?: SocialProfileField[];
  requireLinkedIn?: boolean;
  applicationFields?: {
    socialProfiles?: SocialProfileField[];
    requireLinkedin?: boolean;
    requireLinkedIn?: boolean;
  };
}): SocialProfileField[] {
  const fromFields = raw.applicationFields?.socialProfiles ?? raw.socialProfiles;
  if (fromFields?.length) return fromFields;

  const legacy =
    raw.requireLinkedIn ??
    raw.applicationFields?.requireLinkedin ??
    raw.applicationFields?.requireLinkedIn;
  if (legacy === true) {
    return [{ type: 'linkedin', required: true }];
  }

  return [];
}

export function canAddSocialProfile(fields: SocialProfileField[]): boolean {
  return fields.length < MAX_SOCIAL_PROFILES;
}

export function availableSocialProfileTypes(fields: SocialProfileField[]): SocialProfileType[] {
  const used = new Set(
    fields
      .filter((f) => f.type !== 'custom')
      .map((f) => f.type),
  );
  return SOCIAL_PROFILE_TYPES.filter((t) => t === 'custom' || !used.has(t));
}

export function defaultSocialProfileField(type: SocialProfileType): SocialProfileField {
  return { type, required: false, ...(type === 'custom' ? { customLabel: '' } : {}) };
}

export function socialProfileLabel(
  field: SocialProfileField & { label?: string },
  t: (key: string) => string,
): string {
  if (field.type === 'custom') {
    const custom = field.customLabel?.trim() || field.label?.trim();
    if (custom) return custom;
  }
  return t(`positions.socialType.${field.type}`);
}

export function socialProfileSubmissionLabel(
  submission: SocialProfileSubmission,
  t: (key: string) => string,
): string {
  if (submission.type === 'custom' && submission.label?.trim()) {
    return submission.label.trim();
  }
  return t(`positions.socialType.${submission.type}`);
}

/** Merges legacy linkedinUrl with socialProfiles from candidateSnapshot. */
export function resolveCandidateSocialProfiles(
  app: Pick<Application, 'candidateLinkedinUrl' | 'candidateSocialProfiles'>,
): SocialProfileSubmission[] {
  const profiles = [...(app.candidateSocialProfiles ?? [])];
  const hasLinkedIn = profiles.some((profile) => profile.type === 'linkedin');
  if (app.candidateLinkedinUrl?.trim() && !hasLinkedIn) {
    profiles.unshift({ type: 'linkedin', url: app.candidateLinkedinUrl.trim() });
  }
  return profiles.filter((profile) => profile.url?.trim());
}

export function socialProfilePlaceholder(type: SocialProfileType): string {
  switch (type) {
    case 'linkedin':
      return 'https://linkedin.com/in/...';
    case 'instagram':
      return 'https://instagram.com/...';
    case 'facebook':
      return 'https://facebook.com/...';
    case 'tiktok':
      return 'https://tiktok.com/@...';
    case 'website':
      return 'https://...';
    default:
      return 'https://...';
  }
}

function isLikelyLinkedInUrl(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  if (!v) return false;
  if (v.includes(' ')) return false;
  if (v.includes('linkedin.com/in/')) return true;
  if (/^[a-z0-9][a-z0-9-_]{2,99}$/.test(v) && !v.includes('.')) return true;
  return false;
}

function isLikelyUrl(raw: string): boolean {
  const v = raw.trim();
  if (!v || v.includes(' ')) return false;
  try {
    const url = v.startsWith('http') ? v : `https://${v}`;
    const parsed = new URL(url);
    return Boolean(parsed.hostname.includes('.'));
  } catch {
    return false;
  }
}

export function validateSocialProfileUrl(type: SocialProfileType, raw: string): boolean {
  const v = raw.trim();
  if (!v) return false;
  if (type === 'linkedin') return isLikelyLinkedInUrl(v);
  return isLikelyUrl(v);
}

export function buildSocialProfileSubmissions(
  configured: SocialProfileField[],
  values: Record<string, string>,
): SocialProfileSubmission[] {
  return configured
    .map((field) => {
      const key = socialProfileFieldKey(field);
      const url = values[key]?.trim() ?? '';
      if (!url) return null;
      return {
        type: field.type,
        ...(field.type === 'custom' && field.customLabel ? { label: field.customLabel } : {}),
        url,
      };
    })
    .filter((entry): entry is SocialProfileSubmission => entry !== null);
}

export function initialSocialProfileValues(fields: SocialProfileField[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [socialProfileFieldKey(f), '']));
}
